import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  FileDownload as ExportIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Receipt,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Invoice, apiService } from '../../services/apiService';

const InvoicesManagement: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Invoice>>({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getInvoices();
      setInvoices(response.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const handleAddNew = () => {
    navigate('/invoices/add');
  };

  const handleEditClick = (invoice?: Invoice) => {
    if (invoice) {
      navigate(`/invoices/edit/${invoice.id}`);
    }
  };

  const handleDeleteClick = (invoice?: Invoice) => {
    if (invoice) {
      Swal.fire({
        title: 'Are you sure?',
        text: `Are you sure you want to delete invoice "${invoice.invoice_number}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await apiService.deleteInvoice(invoice.id);
            toast.success('Invoice deleted successfully');
            fetchData();
          } catch (error: any) {
            toast.error(error.message || 'Failed to delete invoice');
          }
        }
      });
    }
  };

  const handleDownloadPDF = (invoice: Invoice) => {
    // Create a professional PDF content
    const pdfContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          @page { margin: 0.5in; }
          body { 
            font-family: 'Arial', sans-serif; 
            margin: 0; 
            padding: 20px; 
            font-size: 12px;
            line-height: 1.4;
          }
          .header { 
            text-align: center; 
            margin-bottom: 30px; 
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .header h1 { 
            margin: 0; 
            font-size: 28px; 
            color: #333; 
            font-weight: bold;
          }
          .header h2 { 
            margin: 5px 0 0 0; 
            font-size: 18px; 
            color: #666; 
            font-weight: normal;
          }
          .invoice-details { 
            margin-bottom: 30px; 
            display: flex;
            justify-content: space-between;
          }
          .invoice-info, .customer-details { 
            width: 48%; 
          }
          .customer-details { 
            margin-bottom: 30px; 
          }
          .customer-details h3 {
            margin: 0 0 10px 0;
            font-size: 14px;
            color: #333;
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
          }
          .table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 30px; 
            font-size: 11px;
          }
          .table th, .table td { 
            border: 1px solid #333; 
            padding: 8px; 
            text-align: left; 
          }
          .table th { 
            background-color: #f5f5f5; 
            font-weight: bold;
          }
          .total-section { 
            text-align: right; 
            margin-top: 20px; 
            border-top: 2px solid #333;
            padding-top: 10px;
          }
          .total-section .total-amount {
            font-size: 16px;
            font-weight: bold;
            color: #333;
          }
          .footer { 
            margin-top: 50px; 
            text-align: center; 
            color: #666; 
            font-size: 10px;
            border-top: 1px solid #ddd;
            padding-top: 20px;
          }
          .notes {
            margin-top: 20px;
            padding: 10px;
            background-color: #f9f9f9;
            border-left: 4px solid #333;
          }
          .notes h3 {
            margin: 0 0 10px 0;
            font-size: 14px;
            color: #333;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>INVOICE</h1>
          <h2>Invoice #${invoice.invoice_number}</h2>
        </div>
        
        <div class="invoice-details">
          <div class="invoice-info">
            <p><strong>Invoice Date:</strong> ${formatDate(invoice.invoice_date)}</p>
            ${invoice.due_date ? `<p><strong>Due Date:</strong> ${formatDate(invoice.due_date)}</p>` : ''}
            <p><strong>Status:</strong> ${invoice.status.toUpperCase()}</p>
          </div>
          
          ${invoice.customer ? `
          <div class="customer-details">
            <h3>Bill To:</h3>
            <p><strong>${invoice.customer.company_name}</strong></p>
            <p>${invoice.customer.contact_person}</p>
            <p>${invoice.customer.email}</p>
            <p>${invoice.customer.phone}</p>
          </div>
          ` : ''}
        </div>
        
        <table class="table">
          <thead>
            <tr>
              <th style="width: 70%;">Description</th>
              <th style="width: 30%;">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Invoice Total</td>
              <td>AED {formatCurrency(parseFloat(invoice.total_amount?.toString() || '0') || 0)}</td>
            </tr>
          </tbody>
        </table>
        
        <div class="total-section">
          <p class="total-amount">Total Amount: AED {formatCurrency(parseFloat(invoice.total_amount?.toString() || '0') || 0)}</p>
        </div>
        
        ${invoice.notes ? `
        <div class="notes">
          <h3>Notes:</h3>
          <p>${invoice.notes}</p>
        </div>
        ` : ''}
        
        <div class="footer">
          <p>Thank you for your business!</p>
          <p>Generated on ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(pdfContent);
      printWindow.document.close();
      
      // Wait for content to load then trigger print dialog
      printWindow.onload = function() {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      };
    }
    
    toast.success('Invoice PDF ready for download!');
  };

  const handleFormChange = (field: keyof Invoice, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (isEditing && selectedInvoice) {
        await apiService.updateInvoice(selectedInvoice.id, formData);
        toast.success('Invoice updated successfully');
      } else {
        await apiService.createInvoice(formData);
        toast.success('Invoice created successfully');
      }
      setDialogOpen(false);
      setFormData({});
      setIsEditing(false);
      setSelectedInvoice(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save invoice');
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setFormData({});
    setIsEditing(false);
    setSelectedInvoice(null);
  };

  // Filter invoices based on search term and status
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer?.contact_person?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const paginatedInvoices = filteredInvoices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return { bg: '#f3f4f6', color: '#6b7280' };
      case 'sent':
        return { bg: '#dbeafe', color: '#2563eb' };
      case 'paid':
        return { bg: '#e0f2f1', color: '#0d9488' };
      case 'overdue':
        return { bg: '#fee2e2', color: '#dc2626' };
      case 'cancelled':
        return { bg: '#fee2e2', color: '#dc2626' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount);
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Invoices Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New Invoice
        </Button>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                    Total Invoices
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#1f2937', fontWeight: 'bold' }}>
                    {invoices.length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '12px', 
                  backgroundColor: '#e0f2f1', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Receipt sx={{ color: '#0d9488', fontSize: 24 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                    Paid Invoices
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#1f2937', fontWeight: 'bold' }}>
                    {invoices.filter(invoice => invoice.status === 'paid').length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '12px', 
                  backgroundColor: '#e0f2f1', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Typography variant="h6" sx={{ color: '#0d9488', fontWeight: 'bold' }}>
                    {invoices.filter(invoice => invoice.status === 'paid').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#1f2937', fontWeight: 'bold' }}>
                    {formatCurrency(invoices.reduce((sum, invoice) => sum + (parseFloat(invoice.total_amount?.toString() || '0') || 0), 0))}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '12px', 
                  backgroundColor: '#e0f2f1', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Typography variant="h6" sx={{ color: '#0d9488', fontWeight: 'bold' }}>
                    AED {invoices.reduce((sum, invoice) => sum + (parseFloat(invoice.total_amount?.toString() || '0') || 0), 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ 
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
        borderRadius: '16px',
        backgroundColor: 'white',
        border: 'none'
      }}>
        <CardContent sx={{ p: 3 }}>
          {/* Search and Action Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <TextField
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#f8fafc',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #e2e8f0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #cbd5e1',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #4A90E2',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#6b7280' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 120, height: '48px' }}>
              <InputLabel>Filter</InputLabel>
              <Select
                label="Filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ 
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#f8fafc',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #e2e8f0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #cbd5e1',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #4A90E2',
                  },
                }}
              >
                <MenuItem value="all">All Invoices</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              sx={{
                height: '48px',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 500,
                borderColor: '#e2e8f0',
                color: '#64748b',
                px: 3,
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              Export
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0', borderRadius: '12px', height: 'calc(100vh - 450px)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell padding="checkbox">
                    <Checkbox />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Invoice Number</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInvoices.map((invoice) => (
                  <TableRow key={invoice.id} hover sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                    <TableCell padding="checkbox">
                      <Checkbox />
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>{invoice.invoice_number}</TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {invoice.customer?.company_name || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {formatDate(invoice.invoice_date)}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {invoice.due_date ? formatDate(invoice.due_date) : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {formatCurrency(parseFloat(invoice.total_amount?.toString() || '0') || 0)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(invoice.status).bg,
                          color: getStatusColor(invoice.status).color,
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleEditClick(invoice)}
                          sx={{
                            backgroundColor: '#e0f2f1',
                            color: '#0d9488',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2,
                            py: 1,
                            '&:hover': {
                              backgroundColor: '#ccedea',
                            },
                          }}
                        >
                          Update
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleDownloadPDF(invoice)}
                          sx={{
                            backgroundColor: '#f3e8ff',
                            color: '#7c3aed',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2,
                            py: 1,
                            '&:hover': {
                              backgroundColor: '#e9d5ff',
                            },
                          }}
                        >
                          Download
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleDeleteClick(invoice)}
                          sx={{
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2,
                            py: 1,
                            '&:hover': {
                              backgroundColor: '#fecaca',
                            },
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredInvoices.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default InvoicesManagement;